/*
  Collection grid driver — sections/collection.liquid
  ---------------------------------------------------
  Two jobs, both built on the Section Rendering API and both degrading to a normal
  page load when this file is blocked:

    • Load More. Fetches the next page, appends only its product cards, and swaps
      the footer for the fetched one. Swapping rather than rebuilding is why there
      is no string assembly here: the fetched page already carries the right
      "viewed N of M" copy, in the shopper's locale, and the right next-page link.

    • Filter and sort. Re-renders the results region from a URL. The filter UI is a
      separate piece of work, so this exposes a contract rather than owning any
      controls:

        document.dispatchEvent(new CustomEvent('collection:update', {
          detail: { url: '/collections/mens?filter.v.option.color=Black' }
        }))

      and, declaratively, a form marked `data-collection-filters` or a link marked
      `data-collection-filter-link` anywhere on the page. `popstate` is handled, so
      Back and Forward walk the filter history without a reload.

  Both paths finish by dispatching `product-grid:updated` with the changed root, so
  assets/product-card.js can re-tick compare boxes and re-evaluate slider arrows for
  markup that was not in the document when it initialised.
*/
(function () {
  if (window.__productGrid) return;
  window.__productGrid = true;

  function results() {
    return document.querySelector('[data-grid-results]');
  }

  function sectionUrl(url, sectionId) {
    var parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('section_id', sectionId);
    return parsed.toString();
  }

  function setBusy(root, busy) {
    root.setAttribute('data-grid-busy', busy ? 'true' : 'false');
    root.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function announce(root) {
    document.dispatchEvent(
      new CustomEvent('product-grid:updated', { detail: { root: root }, bubbles: true })
    );
  }

  function fetchSection(url, sectionId, wholeDocument, signal) {
    return fetch(sectionUrl(url, sectionId), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: signal,
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Section request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        // Section Rendering returns the section wrapped in its own element, so the
        // region we want is a descendant rather than the root of the response.
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        // The facet refresh wants the drawer, which sits outside the results region.
        if (wholeDocument) return parsed;
        var region = parsed.querySelector('[data-grid-results]');
        if (!region) throw new Error('Section response had no results region');
        return region;
      });
  }

  /* ---------- Request ordering ---------- */

  /* One sequence over both paths, because a re-render replaces the grid a Load More
     was going to append to. Only a re-render supersedes; Load More just checks its
     number still holds before touching the DOM. */
  var seq = 0;
  var inFlight = [];

  function abortable() {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    if (controller) inFlight.push(controller);
    return controller;
  }

  function supersede() {
    for (var i = 0; i < inFlight.length; i++) inFlight[i].abort();
    inFlight = [];
    return ++seq;
  }

  function settle(mine) {
    if (mine !== seq) return false;
    inFlight = [];
    return true;
  }

  /* ---------- Load More ---------- */

  function loadMore(button) {
    var root = results();
    if (!root || root.getAttribute('data-grid-busy') === 'true') return;

    var href = button.getAttribute('href');
    if (!href) return;

    setBusy(root, true);
    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');

    var mine = seq;
    var controller = abortable();

    fetchSection(href, root.getAttribute('data-section-id'), false, controller && controller.signal)
      .then(function (region) {
        if (mine !== seq) return;

        var list = root.querySelector('[data-grid-items]');
        var incoming = region.querySelectorAll('[data-grid-items] > .product-grid__item');
        if (!list || !incoming.length) return;

        // Product cards only. The promotional tiles come back in every response, and
        // appending them again would duplicate every one of them per page loaded.
        var first = incoming[0];
        for (var i = 0; i < incoming.length; i++) list.appendChild(incoming[i]);

        var footer = root.querySelector('[data-grid-footer]');
        var nextFooter = region.querySelector('[data-grid-footer]');
        if (footer && nextFooter) {
          footer.replaceWith(nextFooter);
        } else if (footer) {
          // Last page: no footer came back, so there is nothing left to load.
          footer.remove();
        }

        announce(list);

        // Keyboard users land on the first product they just asked for rather than
        // at the top of a longer page with no idea what changed.
        var landing = first.querySelector('a');
        if (landing) landing.focus({ preventScroll: true });
      })
      .catch(function () {
        // A filter superseded this and aborted it, which is not a failure.
        if (mine !== seq) return;
        // Let the anchor do what it always could: go to the next page.
        window.location.href = href;
      })
      .finally(function () {
        // A superseded Load More owns none of this: the re-render is still working,
        // and this button was thrown away with the grid it belonged to.
        if (!settle(mine)) return;
        setBusy(root, false);
        // Cleared here, not per-path: a response that parses but carries no cards
        // returns early, and would otherwise leave the button spinning for good.
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      });
  }

  /* ---------- Filter and sort ---------- */

  /* The drawer lives outside the results region, so a re-render leaves its counts and
     its footer CTA showing the previous numbers. This refetches just the drawer and
     swaps it, keeping which accordions the shopper had open — the whole point of
     applying on tick is that the panel updates around them while it stays open. */
  /* Applies an already-fetched document to the drawer — by morphing the existing DOM,
     never by replacing it. `form.innerHTML = next` destroyed the scrolled body's
     contents mid-interaction: the checkbox that was just ticked (often the element
     with focus) was thrown away, every <details> was recreated closed, and the
     scroller lost its position — which is exactly the breakage that fired the moment
     a filter was ticked inside a long, scrolled accordion. Updating text and state on
     the nodes already in the DOM leaves focus, open accordions and scroll untouched,
     and is also the cheap path: a handful of text writes instead of re-parsing and
     re-laying-out the whole panel.

     The one structural change is the applied-chips row appearing or disappearing at
     the top. That shifts everything below it, so the scroller is compensated by
     exactly the height that appeared above the reading position. */
  function applyFacets(doc) {
    var form = document.querySelector('[data-filters-refresh]');
    var next = doc.querySelector('[data-filters-refresh]');
    if (!form || !next) return;

    var scroller = form.closest('[data-drawer-body]');
    var scrollBefore = scroller ? scroller.scrollTop : 0;

    // Applied chips: the only part swapped wholesale, measuring the height delta so
    // the scroll can be compensated.
    var oldChips = form.querySelector('.facets__applied');
    var newChips = next.querySelector('.facets__applied');
    var hBefore = oldChips ? oldChips.offsetHeight : 0;
    if (oldChips && newChips) oldChips.replaceWith(newChips);
    else if (oldChips) oldChips.remove();
    else if (newChips) form.insertBefore(newChips, form.querySelector('.facets__group'));
    var chipsNow = form.querySelector('.facets__applied');
    var hAfter = chipsNow ? chipsNow.offsetHeight : 0;

    // Groups morph by index; rows match on the input's name and value, so reordering
    // or missing rows degrade to "left as they were" rather than to breakage.
    var oldGroups = form.querySelectorAll('.facets__group');
    var newGroups = next.querySelectorAll('.facets__group');
    for (var g = 0; g < oldGroups.length && g < newGroups.length; g++) {
      var rowByKey = {};
      var newRows = newGroups[g].querySelectorAll('.facets__row');
      for (var n = 0; n < newRows.length; n++) {
        var ni = newRows[n].querySelector('.facets__control');
        if (ni) rowByKey[ni.name + '|' + ni.value] = newRows[n];
      }

      var oldRows = oldGroups[g].querySelectorAll('.facets__row');
      for (var r = 0; r < oldRows.length; r++) {
        var input = oldRows[r].querySelector('.facets__control');
        if (!input) continue;
        var match = rowByKey[input.name + '|' + input.value];
        if (!match) continue;
        var matchInput = match.querySelector('.facets__control');

        // The control the shopper is on keeps its state: the server echo agrees with
        // it anyway, and writing checked onto the active element kills a second click
        // that is already in flight.
        if (document.activeElement !== input) input.checked = matchInput.checked;
        input.disabled = matchInput.disabled;
        oldRows[r].classList.toggle('is-unavailable', match.classList.contains('is-unavailable'));

        var oldCount = oldRows[r].querySelector('.facets__row-count');
        var newCount = match.querySelector('.facets__row-count');
        if (oldCount && newCount && oldCount.textContent !== newCount.textContent) {
          oldCount.textContent = newCount.textContent;
        }
      }

      // Price: swapped whole only while nobody is holding a handle — mid-drag the
      // shopper's hand wins over the server echo.
      var oldPrice = oldGroups[g].querySelector('[data-facet-price]');
      var newPrice = newGroups[g].querySelector('[data-facet-price]');
      if (oldPrice && newPrice && !oldPrice.contains(document.activeElement)) {
        oldPrice.replaceWith(newPrice);
        syncPrice(oldGroups[g].querySelector('[data-facet-price]'), false);
      }
    }

    // The footer sits outside the scroller, so a wholesale swap moves nothing.
    var footer = document.querySelector('[data-drawer] .drawer__footer');
    var nextFooter = doc.querySelector('.drawer__footer');
    if (footer && nextFooter) footer.innerHTML = nextFooter.innerHTML;

    /* The count beside the Filter & Sort trigger lives in the toolbar, which
       re-renders deliberately leave untouched so the shopper's controls survive —
       which also meant nothing ever wrote the new total into it. The fetched
       document carries the fresh figure; copy it into every counter on the page. */
    var nextCount = doc.querySelector('[data-grid-count]');
    if (nextCount) {
      var counts = document.querySelectorAll('[data-grid-count]');
      for (var c = 0; c < counts.length; c++) {
        if (counts[c].textContent !== nextCount.textContent) {
          counts[c].textContent = nextCount.textContent;
        }
      }
    }

    /* The applied-filter pill on the trigger, same story: it sits in the toolbar, so
       a re-render never reaches it. Copied wholesale because the markup carries both
       the number and the screen-reader phrase, already pluralised by Liquid. */
    var nextApplied = doc.querySelector('[data-filter-applied-count]');
    if (nextApplied) {
      var pills = document.querySelectorAll('[data-filter-applied-count]');
      for (var p = 0; p < pills.length; p++) {
        pills[p].innerHTML = nextApplied.innerHTML;
        pills[p].toggleAttribute('hidden', nextApplied.hasAttribute('hidden'));
      }
    }

    if (scroller && hAfter !== hBefore) {
      scroller.scrollTop = scrollBefore + (hAfter - hBefore);
    }
  }

  function render(url, options) {
    var root = results();
    if (!root) return;

    var push = !options || options.push !== false;
    setBusy(root, true);

    // Newest tick wins. Load More can bail while busy, but a filter cannot — that
    // drops the tick the shopper just made — so a re-render supersedes instead.
    var mine = supersede();
    var controller = abortable();

    // One request, both regions. The drawer and the grid come out of the same
    // response, so the panel updates a frame after it arrives rather than waiting on
    // a second round trip of its own.
    fetchSection(url, root.getAttribute('data-section-id'), true, controller && controller.signal)
      .then(function (doc) {
        if (mine !== seq) return;

        var region = doc.querySelector('[data-grid-results]');
        if (!region) throw new Error('Section response had no results region');

        applyFacets(doc);
        root.innerHTML = region.innerHTML;
        if (push) window.history.pushState({ productGrid: true }, '', url);
        announce(root);
        document.dispatchEvent(
          new CustomEvent('collection:updated', { detail: { url: url }, bubbles: true })
        );
      })
      .catch(function () {
        // A superseded request rejects on its own abort, which is not a failure.
        if (mine !== seq) return;
        window.location.href = url;
      })
      .finally(function () {
        // Only the newest request owns the busy state, or an abandoned one clears it
        // while its replacement is still loading.
        if (!settle(mine)) return;
        setBusy(root, false);
      });
  }

  /* ---------- Wiring ---------- */

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    var more = e.target.closest('[data-grid-load-more]');
    if (more) {
      e.preventDefault();
      loadMore(more);
      return;
    }

    var filterLink = e.target.closest('a[data-collection-filter-link]');
    if (filterLink && filterLink.href) {
      e.preventDefault();
      render(filterLink.href);
    }
  });

  function submitFilters(form) {
    // Drop empty values, or an untouched price range would pin every URL to the full
    // span and the "clear all" link would never actually clear anything.
    var data = new FormData(form);
    var query = new URLSearchParams();
    data.forEach(function (value, key) {
      if (value !== '' && value != null) query.append(key, value);
    });
    var action = form.getAttribute('action') || window.location.pathname;
    var qs = query.toString();
    render(action.split('?')[0] + (qs ? '?' + qs : ''));
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.hasAttribute || !form.hasAttribute('data-collection-filters')) return;
    e.preventDefault();
    submitFilters(form);
  });

  /* Ticking a box applies it immediately — the drawer stays open and the count in its
     footer updates, which is the whole point of filtering with the panel in front of
     you. The footer CTA is then just "I am done", not "apply". */
  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.closest) return;
    var form = e.target.closest('form[data-collection-filters]');
    if (!form) return;
    // The price handles fire `change` too, but they maintain their own hidden inputs
    // first — see the price section below, which calls this once it has.
    if (e.target.hasAttribute('data-price-thumb')) return;
    submitFilters(form);
  });

  /* ---------- Price range ---------- */

  /* Builds the design's string: currency code, space, then the shop's own money
     format - "CAD $630.00". Two separate bugs lived here. Intl.NumberFormat renders
     "CA$630.00" because it chooses its own currency presentation, and
     money_with_currency follows the shop setting, which on this store puts the code
     last ("$630.00 CAD"). The design states code first, so it is assembled rather
     than delegated. Falls back to Intl only when no format was handed down. */
  function money(amount, root) {
    var fmt = root && root.getAttribute("data-money-format");
    var iso = root && root.getAttribute("data-currency");
    if (fmt) {
      /* Zero pence are dropped, the rule snippets/price.liquid applies on the server:
         "CAD $630", but "CAD $7.01". The slider works in whole units, so this is the
         common case rather than the edge one. */
      var whole = Math.abs(amount - Math.round(amount)) < 0.005;
      var group = function (n) {
        return n.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      };
      var withDecimals = group(whole ? String(Math.round(amount)) : amount.toFixed(2));
      var noDecimals = group(String(Math.round(amount)));
      /* A symbol-less currency's format spells the code, "CHF {{amount}}"; it is
         taken out so the code is only ever the prefix, as snippets/price.liquid does. */
      if (iso) fmt = fmt.replace(iso, "").trim();
      return (iso ? iso + " " : "") + fmt
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDecimals)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
        .replace(/\{\{\s*amount\s*\}\}/g, withDecimals);
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: root && root.getAttribute("data-currency")
      }).format(amount);
    } catch (err) {
      return String(amount);
    }
  }

  function syncPrice(root, submit) {
    var min = root.querySelector('[data-price-thumb="min"]');
    var max = root.querySelector('[data-price-thumb="max"]');
    if (!min || !max) return;

    // The two handles share a track, so they have to be stopped from crossing.
    var lo = Math.min(Number(min.value), Number(max.value));
    var hi = Math.max(Number(min.value), Number(max.value));
    min.value = String(lo);
    max.value = String(hi);

    var ceiling = Number(root.getAttribute('data-ceiling')) || 0;
    var fill = root.querySelector('[data-price-fill]');
    if (fill && ceiling > 0) {
      fill.style.insetInlineStart = (lo / ceiling) * 100 + '%';
      fill.style.width = ((hi - lo) / ceiling) * 100 + '%';
    }

    var loLabel = root.querySelector('[data-price-label="min"]');
    var hiLabel = root.querySelector('[data-price-label="max"]');
    if (loLabel) loLabel.textContent = money(lo, root);
    if (hiLabel) hiLabel.textContent = money(hi, root);

    /* Currency units, not cents. The two are mixed in this API and getting it
       backwards is silent: `range_max` and `min_value.value` come back in cents, but
       the `filter.v.price.*` URL parameter is read in whole currency units. Writing
       cents here meant a $630 cap went out as `lte=63000`, came back understood as
       $63,000, and the slider redrew itself against a ceiling a hundred times too
       high — which is what looked like the range resetting. An untouched end still
       contributes nothing at all. */
    var loInput = root.querySelector('[data-price-input="min"]');
    var hiInput = root.querySelector('[data-price-input="max"]');
    if (loInput) loInput.value = lo > 0 ? String(lo) : '';
    if (hiInput) hiInput.value = hi < ceiling ? String(hi) : '';

    if (submit) {
      var form = root.closest('form[data-collection-filters]');
      if (form) submitFilters(form);
    }
  }

  // `input` while dragging keeps the fill and the labels live; `change` on release is
  // what actually re-renders the grid, so a drag is one request rather than fifty.
  document.addEventListener('input', function (e) {
    if (!e.target || !e.target.hasAttribute || !e.target.hasAttribute('data-price-thumb')) return;
    var root = e.target.closest('[data-facet-price]');
    if (root) syncPrice(root, false);
  });

  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.hasAttribute || !e.target.hasAttribute('data-price-thumb')) return;
    var root = e.target.closest('[data-facet-price]');
    if (root) syncPrice(root, true);
  });

  document.addEventListener('drawer:open', function (e) {
    var prices = e.target.querySelectorAll ? e.target.querySelectorAll('[data-facet-price]') : [];
    for (var i = 0; i < prices.length; i++) syncPrice(prices[i], false);
  });

  document.addEventListener('collection:update', function (e) {
    var url = e.detail && e.detail.url;
    if (url) render(url, { push: e.detail.push !== false });
  });

  // Back and Forward through the filter history. Guarded on the marker this script
  // writes, so an unrelated history entry still gets a real navigation.
  window.addEventListener('popstate', function (e) {
    if (!e.state || !e.state.productGrid) return;
    render(window.location.href, { push: false });
  });

  // The entry the page loaded on carries no state of its own, so stepping Back to it
  // from a filtered one would change the URL and leave the old results on screen.
  // Marking it is what makes the first Back re-render.
  function markEntry() {
    if (!results()) return;
    if (window.history.state && window.history.state.productGrid) return;
    window.history.replaceState({ productGrid: true }, '', window.location.href);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', markEntry);
  } else {
    markEntry();
  }
})();
